import { useEffect, useRef, useState } from 'react';
import { api } from '../../api/client';
import type { Personne } from '../../types';

interface PersonneFiltres {
  externe?: boolean;
  actif?: boolean;
  pole_id?: number;
}

// Combobox personne : un seul champ, les suggestions s'affichent sous la
// saisie au fil de la frappe (filtre client, l'annuaire reste petit).
// Sélection au clic ou au clavier (↑ ↓ Entrée, Échap pour fermer).
// Les composants ne font jamais fetch() eux-mêmes : on passe par api.getPersonnes.
export function PersonneSelect({
  id, label, ariaLabelledby, value, onChange, filtres, placeholder = 'Rechercher un nom…', required, disabled,
}: {
  id: string;
  label?: string;
  /** Titre déjà affiché ailleurs qui nomme le champ (sans lui, le nom accessible serait le texte d'exemple). */
  ariaLabelledby?: string;
  value: number | null;
  onChange: (id: number | null) => void;
  filtres?: PersonneFiltres;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
}) {
  const [personnes, setPersonnes] = useState<Personne[]>([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [texte, setTexte] = useState('');
  const [ouvert, setOuvert] = useState(false);
  const [indexActif, setIndexActif] = useState(0);
  const conteneurRef = useRef<HTMLDivElement>(null);
  const cleFiltres = JSON.stringify(filtres ?? {});

  useEffect(() => {
    let annule = false;
    setLoading(true);
    setErreur(null);
    api.getPersonnes(filtres ?? {})
      .then((p) => { if (!annule) setPersonnes(p); })
      .catch((err: unknown) => { if (!annule) setErreur(err instanceof Error ? err.message : 'Impossible de charger les personnes.'); })
      .finally(() => { if (!annule) setLoading(false); });
    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleFiltres]);

  // La valeur peut être posée de l'extérieur (auto-présélection de la personne
  // connectée) : refléter son nom dans le champ.
  useEffect(() => {
    if (value == null) return;
    const p = personnes.find((x) => x.id === value);
    if (p) setTexte(p.display_name);
  }, [value, personnes]);

  // La valeur est remise à zéro de l'extérieur (« Nouvelle demande ») alors que le champ n'a pas le focus : le nom de la personne précédente ne doit pas rester affiché
  // (il l'était, avec « Personne absente (à faire) » dessous, et un lecteur d'écran l'annonçait comme valeur).
  const saisie = useRef<HTMLInputElement>(null);
  useEffect(() => { if (value == null && document.activeElement !== saisie.current) setTexte(''); }, [value]);

  // Sélection déjà faite → au focus on montre toute la liste (le texte affiché
  // est le nom complet, le filtrer donnerait une liste d'un seul élément).
  const q = value == null ? texte.trim().toLowerCase() : '';
  const filtrees = q ? personnes.filter((p) => p.display_name.toLowerCase().includes(q)) : personnes;

  function choisir(p: Personne) {
    onChange(p.id);
    setTexte(p.display_name);
    setOuvert(false);
  }

  function onSaisie(v: string) {
    setTexte(v);
    setIndexActif(0);
    setOuvert(true);
    if (value != null) onChange(null); // modifier le texte invalide la sélection
    if (v === '') onChange(null);
  }

  function onTouche(e: React.KeyboardEvent) {
    if (!ouvert && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) { setOuvert(true); return; }
    if (!ouvert) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setIndexActif((i) => Math.min(i + 1, filtrees.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setIndexActif((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (filtrees[indexActif]) choisir(filtrees[indexActif]); }
    else if (e.key === 'Escape') { setOuvert(false); }
  }

  return (
    <div className="form-group" ref={conteneurRef} style={{ position: 'relative' }}>
      {label && <label className="form-label" htmlFor={id}>{label}{required && ' *'}</label>}
      <input
        ref={saisie}
        id={id}
        type="text"
        className="form-input"
        placeholder={loading ? 'Chargement…' : placeholder}
        value={texte}
        onChange={(e) => onSaisie(e.target.value)}
        onFocus={() => setOuvert(true)}
        onBlur={() => setTimeout(() => setOuvert(false), 120)}
        onKeyDown={onTouche}
        role="combobox"
        aria-labelledby={ariaLabelledby}
        aria-expanded={ouvert}
        aria-controls={`${id}-liste`}
        aria-required={required}
        autoComplete="off"
        disabled={disabled || loading}
      />
      {ouvert && !loading && (
        <div
          id={`${id}-liste`}
          role="listbox"
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30,
            background: 'var(--surface)', border: '1px solid var(--line)',
            borderRadius: 'var(--r1)', marginTop: 2, maxHeight: 240, overflowY: 'auto',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          }}
        >
          {filtrees.length === 0 ? (
            <div className="text-xs text-ink-3" style={{ padding: '8px 12px' }}>Aucun résultat.</div>
          ) : (
            filtrees.map((p, i) => (
              <div
                key={p.id}
                role="option"
                aria-selected={p.id === value}
                onMouseDown={(e) => { e.preventDefault(); choisir(p); }}
                onMouseEnter={() => setIndexActif(i)}
                className="text-sm"
                style={{
                  padding: '6px 12px',
                  cursor: 'pointer',
                  background: i === indexActif ? 'var(--accent-dim)' : p.id === value ? 'var(--accent-dim)' : 'transparent',
                }}
              >
                {p.display_name}{p.is_externe ? <span className="text-ink-3"> (externe)</span> : ''}
              </div>
            ))
          )}
        </div>
      )}
      {erreur && <div className="text-xs text-err mt-4">{erreur}</div>}
    </div>
  );
}
